import {
  GtagScript,
  GtagScriptProps,
  GTMScript,
  GTMScriptProps,
  ScriptTypes,
  SupportedSript,
  TrackingScript,
  TrackingScriptProps,
  TrackingScriptsConfig,
  UsercentricsScript,
  UsercentricsScriptProps,
} from './tracking.model';

import getGTMScript from './scripts/gtm';
import getGtagScript from './scripts/gtag';
import getUsercentricsScript from './scripts/usercentrics';

/**
 * Build a list of scripts containing usercentrics, gtm, gtag and other tracking scripts configured by the user.
 * Connect them to usercentrics while making sure they don't execute until consent is given.
 */
export class Tracking {
  customEvents: any[] = [];

  /** Order Matters
   * The Usercentrics script has to be inserted BEFORE the Google Tag Manager script or Gtag Script
   * @param trackingConfigs
   * @returns array of scripts that can be used with vue-meta to load scripts
   */
  toObjects(trackingConfigs: TrackingScriptsConfig): SupportedSript[] {
    return [
      this.buildUsercentricsScript(trackingConfigs.usercentrics),
      this.buildGTMScript(trackingConfigs?.gtm),
      this.buildGtagScript(trackingConfigs?.gtag),
      ...this.buildOtherScripts(trackingConfigs?.scripts),
    ].filter(Boolean) as SupportedSript[];
  }

  /**
   * Order Matters
   * The Usercentrics script has to be inserted BEFORE the Google Tag Manager script or Gtag Script
   * @param trackingConfigs
   * @returns Scripts as HTML Strings
   */
  toHTML(trackingConfigs: TrackingScriptsConfig): string[] {
    return this.toObjects(trackingConfigs).map(this.scriptToHTML);
  }

  /**
   * When a container is loaded, Tag Manager will begin to process all queued data layer push messages.
   * Tag Manager processes messages on a first-in, first-out basis: Each message is processed one at a time, in the order it was received.
   * If the message is an event, any tags with triggers conditions that have been met will fire before Tag Manager moves on to the next message.
   * @param data can be used to pass any information to tags.
   * Events or variables can be passed via the data layer, and triggers can be set up based on the values of variables.
   * https://developers.google.com/tag-platform/tag-manager/web/datalayer
   */
  pushToDataLayer(data: any): void {
    if (this.isEmptyObject(data)) {
      return;
    }

    if (window && window['dataLayer']) {
      this.sendPendingCustomEventsToDatalayer();
      window['dataLayer'].push(data);
    } else {
      this.customEvents.push(data);
    }
  }

  /**
   *
   * @param script
   * @returns HTML string for a given script
   */
  private scriptToHTML(script: SupportedSript): string {
    const scriptAttributesString = Object.entries(script)
      .filter(([, value]) => Boolean(value))
      .reduce((html, [key, value]) => {
        return html + `${key}="${value}" `;
      }, '');
    return `<script ${scriptAttributesString} >${script.innerHTML ?? ''}</script>`;
  }

  /**
   * Builds Usercentric script which can be inserted to the HEAD section of the website.
   * @param uc
   * @returns usercentric script when usercentric configration is defined and isIncluded is true else returns null
   */
  private buildUsercentricsScript(uc: TrackingScriptsConfig['usercentrics']): UsercentricsScript | null {
    return uc && this.isIncluded(uc) ? getUsercentricsScript(uc) : null;
  }

  /**
   * Builds google tag manager script which can be inserted to the HEAD section of the website
   * @param gtm
   * @returns Google Tag Manager when gtm configration is defined and isIcluded is true else returns null
   */
  private buildGTMScript(gtm: TrackingScriptsConfig['gtm']): GTMScript | null {
    return gtm && this.isIncluded(gtm) ? getGTMScript(gtm) : null;
  }

  /**
   * Builds gtag script which can be inserted to the HEAD section of the website
   * @param gtag
   * @returns Gtag script when gtag configration is defined and isIncluded is true else returns null
   */
  private buildGtagScript(gtag: TrackingScriptsConfig['gtag']): GtagScript | null {
    return gtag && this.isIncluded(gtag) ? getGtagScript(gtag) : null;
  }

  /**
   * Builds third party scripts that are not supported by GTM.
   * @param scripts
   * @returns custom script when custom script configration is defined and isIncluded is true else return empty array
   */
  private buildOtherScripts(scripts: TrackingScriptsConfig['scripts']): TrackingScript[] {
    if (!scripts) {
      return [];
    }
    return Object.entries(scripts)
      .filter(([, options]) => this.isIncluded(options))
      .map(([, options]) => {
        const serviceName = options.ucServiceName;
        return {
          type: ScriptTypes.PLAIN,
          'data-usercentrics': serviceName,
          ...omit(options, ['ucServiceName', 'includeIf']),
        };
      });
  }

  /**
   *
   * @param config
   * @returns true or false depending on the value passed to isIncluded. Defaults to true
   */
  isIncluded(
    config: UsercentricsScriptProps | GTMScriptProps | GtagScriptProps | TrackingScriptProps | undefined,
  ): boolean {
    return config?.includeIf ?? true;
  }

  /**
   * Pushes any pending custom events to data layer
   */
  sendPendingCustomEventsToDatalayer() {
    if (this.customEvents.length) {
      this.customEvents.reverse().forEach(event => window['dataLayer'].unshift(event));
      this.customEvents.length = 0;
    }
  }

  private isEmptyObject(obj: Record<string, unknown>) {
    return obj && Object.keys(obj).length === 0 && Object.getPrototypeOf(obj) === Object.prototype;
  }
}

export const omit = <T extends Record<string, any>>(obj: T, keys: (keyof T)[]): T => {
  const objectClone = {} as Record<string, any>;
  for (const prop of Reflect.ownKeys(obj).filter(v => typeof v === 'string') as string[]) {
    if (keys.includes(prop)) {
      continue;
    }
    objectClone[prop] = obj[prop];
  }

  return objectClone as T;
};
